/**
 * useSubscriptionNotifications
 * Hook to manage subscription renewal notifications
 */

import { useEffect, useCallback } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useUpcomingRenewals } from './useAccountingSubscriptions';
import { subscriptionNotificationService } from '@/services/accounting/subscription-notification.service';
import { logger } from '@/lib/logger';

interface UseSubscriptionNotificationsOptions {
  /** Number of days to check ahead for renewals (default: 7) */
  daysAhead?: number;
  /** Check interval in minutes (default: 60) */
  checkIntervalMinutes?: number;
  /** Enable automatic notifications (default: true) */
  enabled?: boolean;
}

/**
 * Hook to automatically check for subscription renewals and send notifications
 *
 * @example
 * ```tsx
 * // In your app root or dashboard
 * useSubscriptionNotifications({
 *   daysAhead: 7,
 *   checkIntervalMinutes: 60,
 *   enabled: true
 * });
 * ```
 */
export function useSubscriptionNotifications(options: UseSubscriptionNotificationsOptions = {}) {
  const {
    daysAhead = 7,
    checkIntervalMinutes = 60,
    enabled = true
  } = options;

  const { user } = useAuth();
  const { data: renewals = [], isLoading } = useUpcomingRenewals({
    userId: user?.id,
    days: daysAhead,
  });

  const checkAndNotify = useCallback(async () => {
    if (!user?.id || !enabled || renewals.length === 0) {
      return;
    }

    logger.info('Checking subscription renewals for notifications', {
      userId: user.id,
      renewalCount: renewals.length,
    });

    try {
      const result = await subscriptionNotificationService.processRenewalAlerts(renewals);

      logger.info('Subscription notification check complete', {
        userId: user.id,
        ...result,
      });
    } catch (error) {
      logger.error('Error processing subscription renewal notifications', {
        error,
        userId: user.id,
      });
    }
  }, [user?.id, enabled, renewals]);

  // Check immediately on mount and when renewals change
  useEffect(() => {
    if (!isLoading && enabled) {
      checkAndNotify();
    }
  }, [checkAndNotify, isLoading, enabled]);

  // Set up periodic checks
  useEffect(() => {
    if (!enabled || checkIntervalMinutes <= 0) {
      return;
    }

    const intervalMs = checkIntervalMinutes * 60 * 1000;
    const intervalId = setInterval(checkAndNotify, intervalMs);

    logger.info('Started subscription notification checker', {
      intervalMinutes: checkIntervalMinutes,
      daysAhead,
    });

    return () => {
      clearInterval(intervalId);
      logger.info('Stopped subscription notification checker');
    };
  }, [checkAndNotify, checkIntervalMinutes, daysAhead, enabled]);

  return {
    renewals,
    isLoading,
    checkNow: checkAndNotify,
  };
}
